import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ProfileImageService } from '@/integrations/firebase/fileServices';
import { FirebaseStorageService } from '@/integrations/firebase/storage';
import { Upload, X, Camera, User } from 'lucide-react';

interface ProfileImageUploadProps {
  currentImageUrl?: string;
  onImageUpdate?: (imageUrl: string) => void;
  className?: string;
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  currentImageUrl,
  onImageUpdate,
  className = ''
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file
    const validation = FirebaseStorageService.validateFile(
      file,
      ['image/jpeg', 'image/png', 'image/webp'],
      5 // 5MB max
    );

    if (!validation.valid) {
      toast({
        title: "Invalid file",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadProfileImage(file);
  };

  const uploadProfileImage = async (file: File) => {
    if (!user) return;

    setUploading(true);
    try {
      const result = await ProfileImageService.uploadProfileImage(file, user.uid);
      
      toast({
        title: "Profile image updated",
        description: "Your profile image has been successfully updated.",
      });

      if (onImageUpdate) {
        onImageUpdate(result.url);
      }

      // Clear preview
      setPreviewUrl(null);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error uploading profile image:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload profile image. Please try again.",
        variant: "destructive",
      });
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!user || !currentImageUrl) return;

    try {
      // Get user's personal info to find image paths
      const personalInfoList = await ProfileImageService.getUserProfileImages(user.uid);
      const profileImage = personalInfoList.find(img => img.url === currentImageUrl);
      
      if (profileImage) {
        await ProfileImageService.deleteProfileImage(
          user.uid, 
          profileImage.path, 
          profileImage.path.replace('/images/', '/thumbnails/')
        );
        
        toast({
          title: "Profile image removed",
          description: "Your profile image has been removed.",
        });

        if (onImageUpdate) {
          onImageUpdate('');
        }
      }
    } catch (error: any) {
      console.error('Error removing profile image:', error);
      toast({
        title: "Remove failed",
        description: "Failed to remove profile image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const displayImageUrl = previewUrl || currentImageUrl;

  return (
    <Card className={`w-full max-w-sm ${className}`}>
      <CardHeader>
        <CardTitle className="text-center">Profile Image</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-center">
          <Avatar className="w-32 h-32">
            <AvatarImage src={displayImageUrl || undefined} alt="Profile" />
            <AvatarFallback className="w-32 h-32 text-4xl">
              <User />
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full"
            variant="outline"
          >
            {uploading ? (
              <>
                <Upload className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" />
                {currentImageUrl ? 'Change Image' : 'Upload Image'}
              </>
            )}
          </Button>

          {currentImageUrl && (
            <Button
              onClick={handleRemoveImage}
              variant="destructive"
              className="w-full"
              disabled={uploading}
            >
              <X className="w-4 h-4 mr-2" />
              Remove Image
            </Button>
          )}
        </div>

        <div className="text-xs text-muted-foreground text-center">
          <p>Supported formats: JPEG, PNG, WebP</p>
          <p>Maximum size: 5MB</p>
          <p>Recommended: Square image, 400x400px or larger</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileImageUpload;
